import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import parse from "html-react-parser";
import { useVideo } from "../../../hooks/useVideo";
import ConfirmationModal from "../../confirmation/ConfirmationModal";
import { generatePageNumbers } from "../../../util/generatePageNumber";
import LoadingSpinner from "../../loader/Loader";
import {
  useDeleteProductMutation,
  useGetProductsQuery,
  useUnlinkProductVideoMutation,
} from "../../../features/api/productApiSlice";

const ProductsList = () => {
  const navigate = useNavigate();
  const { formatDate } = useVideo();
  const [deleteProduct] = useDeleteProductMutation();
  const [unlinkProductVideo] = useUnlinkProductVideoMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // categories per page

  const {
    data: products = [], // ✅ safe default so maps never blow up
    isFetching, // use this for spinner
    isError,
    isLoading,
    refetch,
  } = useGetProductsQuery(undefined, {
    refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
    refetchOnFocus: true, // refresh when tab regains focus
    refetchOnReconnect: true, // refresh when network comes back
  });

  useEffect(() => {
    if (!products?.length && !isFetching && !isError) {
      refetch(); // one more nudge in case the first request was aborted in dev
    }
  }, [products?.length, isFetching, isError, refetch]);

  const [productFilter, setProductFilter] = useState("");

  // Filtered transactions
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((tx) => {
      const matchesVideo =
        !productFilter ||
        `${tx?.title}`.toLowerCase().includes(productFilter.toLowerCase());
      return matchesVideo;
    });
  }, [products, productFilter]);

  const totalPages = Math.ceil(filteredProducts.length / pageSize);

  const currentVideos = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredProducts.slice(startIdx, startIdx + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (productId, isLinked) => {
    if (isLinked) {
      toast.warning(
        "Cannot delete product. It is linked to one or more videos."
      );
      return;
    }
    setProductToDelete(productId);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    try {
      const response = await deleteProduct({ id: productToDelete }).unwrap();
      toast.success(response?.message || "Product deleted successfully!");
      setProductToDelete(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to delete product:", err);
      toast.error(err?.data?.message || "Failed to delete product.");
    }
  };

  const handleCancelDelete = () => {
    setProductToDelete(null);
    setIsModalOpen(false);
  };

  const handleUnlinkVideo = async (productId) => {
    try {
      await unlinkProductVideo({ productId: productId }).unwrap();
      toast.success("Product successfully unlinked from videos.");
    } catch (err) {
      console.error("Failed to unlink product:", err);
      toast.error(err?.data?.message || "Failed to unlink product.");
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <p>Error loading transactions.</p>;

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Products</h1>
          <button onClick={() => navigate("/admin/products/add-product")}>
            Add Product
          </button>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by title"
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
        />
      </div>
      {/* ✅ Admin informational message */}
      <div className="admin-info-message">
        <p>
          Clicking the **Unlink Video** button will permanently remove the
          connection between the selected product and all associated videos.
          This action cannot be undone.
        </p>
      </div>
      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Image</th>
            <th>Title</th>
            <th>Price $</th>
            <th>In Stock</th>
            <th>Created</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentVideos?.map((tx, index) => {
            const isLinked = tx?.linkedVideoCount > 0; // ✅ move inside map
            return (
              <tr key={tx?._id}>
                <td>{index + 1}</td>
                <td>
                  <img
                    className="table-image"
                    src={tx?.mainImage}
                    alt={tx?.title}
                  />
                </td>
                <td>{parse(`${tx?.title?.trim().substring(0, 40)}...`)}</td>
                <td>${tx?.price?.toFixed(2)}</td>
                <td style={{ color: tx?.inStock ? "#28BB11" : "#d74a49" }}>
                  {tx?.inStock ? "In Stock" : "Out of Stock"}
                </td>
                <td>{formatDate(tx?.createdAt)}</td>
                <td className="actions-container">
                  <button
                    className="refund-btn"
                    onClick={() => handleDeleteClick(tx?._id || tx?.id)}
                    disabled={isLinked}
                    title={isLinked ? "Cannot delete linked product" : ""}
                  >
                    Delete
                  </button>
                  <Link
                    className="attach-button"
                    aria-label={`attach video ${tx.title}`}
                    to={`/admin/videos/add-video/${tx?._id}`}
                  >
                    Attach Video
                  </Link>
                  {isLinked && (
                    <button
                      className="unlink-button"
                      onClick={() => handleUnlinkVideo(tx?._id)}
                    >
                      Unlink Video
                    </button>
                  )}
                  <Link
                    className="ed-button"
                    aria-label={`edit ${tx?.title}`}
                    to={`/admin/products/edit-product/${tx?._id}`}
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
        <ConfirmationModal
          isOpen={isModalOpen}
          message="Are you sure you want to delete this product? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductsList;
